/**
 * @file   src\Pages\ManageSubscriptions.tsx
 * @brief  ManageSubscriptions page.
 * @date   JUL, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import {
  useState,
  Container,
  Table,
  Badge,
  Tab,
  Tabs,
  Button,
  Breadcrumb,
  useOutletContext,
  useEffect,
  useNavigate,
  Modal,
  OverlayTrigger,
  Tooltip,
  Pagination,
  useParams,
  useLocation,
  React,
} from "../utils/thirdpartyComponents";
import "../styles/subscriptions.scss";
import EditIcon from "../assets/images/icons/editicon";
import DeleteIcon from "../assets/images/icons/deleteicon";
import AddIcon from "../assets/images/icons/addicon";
// import CWSwitch from '../components/CWSwitch';
import { pageURLs } from "../utils/pageUrls";
import { MessageToaster } from "../utils/toastUtil";
import { AppContextType } from "../interfaces/generalInterface";
import CWMultiSelect from "../components/CWMultiSelect";
import axios from "../config/axiosConfig";
import { CompanyApi, LocationApi, SubscriptionApi } from "../utils/apiUrls";
import {
  LocationItem,
  Option,
  SubscriptionItem,
} from "../interfaces/subscriptionInterface";
import GenerateLink from "../components/GenerateLink";
import CarWashStatus from "../components/CarWashStatus";
import SmallLoader from "../components/SmallLoader";
import CWInput from "../components/CWInput";
import copy from "clipboard-copy";
import { EquipmentType, serviceType, userType } from "../utils/enums";
import CWRadio from "../components/CWRadio";
import CWSwitch from "../components/CWSwitch";
import { Popover } from "react-bootstrap";
import { getFromLocalStorage } from "../utils/helper";

const ManageSubscriptions = (props: any) => {
  const carwash_owner =
    props.userType === userType["CARWASH-OWNER"] ? true : false;
  const storageData = getFromLocalStorage('CARWASH_OWNER_DATA');
  const userInfo = storageData && storageData !== null ? storageData : null;
  const isLocationManager = userInfo?.userTypeId === 4 && userInfo?.permissionType === 2;
  const params = useParams();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const toaster = new MessageToaster();
  const [showSubscriptionModal, setShowSubscriptionModal] =
    useState<boolean>(false);
  const [showCopyUrlModal, setShowCopyUrlModal] = useState<boolean>(false);
  const [selectedShareUrl, setSelectedShareUrl] = useState<string>("");
  const [modalAction, setModalAction] = useState<
    "switch" | "deleteBuyup" | "deleteSingleWash" | null
  >(null);
  const [pendingIndex, setPendingIndex] = useState<number | null>(null);
  const [pendingType, setPendingType] = useState<number | null>(null);
  const [pendingBuyupId, setPendingBuyupId] = useState<number | null>(null);
  const [pendingSubscriptionId, setPendingSubscriptionId] = useState<number | null>(
    null
  );
  const [modalMessage, setModalMessage] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [bayType, setbayType] = useState(parseInt(query.get("bayType") || "0") || 0);
  const [totalPages, setTotalPages] = useState(1);
  const [companyDetails, setCompanyDetails] = useState<any>([]);
  const [isMultilocationReportRequired,setIsMultilocationReportRequired] = useState(false);
  
  
  const subscription_type = query.get("type") || "month";
  const [subscriptionActive, setSubscriptionActive] = useState(0);

  // Hide popup
  const handleOnHide = () => {
    setShowSubscriptionModal(false);
    setModalAction(null);
    setPendingIndex(null);
    setPendingType(null);
    setPendingBuyupId(null);
    setPendingSubscriptionId(null);
  };

  const handleCopyUrlClick = (shareUrl?: string | null) => {
    if (!shareUrl) {
      toaster.toastError("Share URL is not available for this subscription.");
      return;
    }
    setSelectedShareUrl(shareUrl);
    setShowCopyUrlModal(true);
  };

  const handleCloseCopyUrlModal = () => {
    setShowCopyUrlModal(false);
    setSelectedShareUrl("");
  };

  const handleCopyPurchaseUrl = () => {
    if (!selectedShareUrl) {
      toaster.toastError("Share URL is not available for this subscription.");
      return;
    }
    copy(selectedShareUrl);
    toaster.toastSuccess("URL copied to clipboard!");
  };

  // State to handle listed subscriptions
  const [subscriptions, setSubscriptions] = useState<SubscriptionItem[]>([]);
  const [buyUps, setBuyUps] = useState<SubscriptionItem[]>([]);

  // State to handle selected location form dropdown
  const [selectedLocation, setSelectedLocation] = useState<Option[]>([]);

  const navigate = useNavigate();

  // Access the context
  const { updateData } = useOutletContext<AppContextType>();

  // State to handle locations given in dropdown
  const [locations, setLocations] = useState<Option[]>([]);

  const [subscriptionType, setSubscriptionType] = useState(subscription_type);

  // State to company details
  const [company, setCompany] = useState({
    companyName: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zipCode: "",
  });

  useEffect(() => {
    getCompanyAddress();
    getCompany();
     if(carwash_owner && !isLocationManager)
     {
    fetchSetting();

     }

    let bayTypeParam  = query.get("bayType")
    if(bayTypeParam!="")
    {
      setbayType(Number(bayTypeParam))
    }
  }, []);

  useEffect(() => {
    if (bayType !== serviceType.Inbay && subscriptionType === "buyups") {
      setSubscriptionType("month");
      setCurrentPage(1);
      return;
    }
    getSubscriptions(currentPage, subscriptionType,bayType); // Fetch and update the subscription list
  }, [currentPage, subscriptionType, selectedLocation,bayType]);

  useEffect(() => {
    handleUpdate();
  }, [company]);

  // To get company details
  const getCompanyAddress = async () => {
    try {
      const id = params.id ? parseInt(params.id) : 0;
      const { data } = carwash_owner
        ? await axios.get(CompanyApi.CARWASH_OWNER_COMPANY_ADDRESS)
        : await axios.get(CompanyApi.GET_COMPANY_ADDRESS + id);
      if (data?.error === 0) {
        setCompany(data.data);
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message);
    }
  };

  // Function to fetch company information
  const getCompany = async () => {
    try {
      const param1 = `${params.id}`;
      const { data } = carwash_owner
        ? await axios.get(CompanyApi.CARWASH_OWNER_COMPANY_DETAILS)
        : await axios.get(CompanyApi.GET_COMPANY + param1);

      // To get locations under company
      await getCompanyLocations(data.data.carWashGUID);
      setCompanyDetails(data.data);
      setSubscriptionActive(data.data.subscriptionActive);
    } catch (error: any) {
      console.error("Error fetching company information:", error.message);
      throw new Error(error.response?.data?.message || error.message);
    }
  };

  const getCompanyLocations = async (carwashGuid: string) => {
    try {
      const { data } = carwash_owner
        ? await axios.get(LocationApi.CARWASH_OWNER_GET_COMPANY_LOCATIONS)
        : await axios.get(LocationApi.GET_COMPANY_LOCATIONS + carwashGuid);
      if (data.error === 0) {
        const location_list: LocationItem[] = data?.data;

        // Transform the array
        const transformedArray: Option[] = location_list.map((item) => ({
          label: item.locationName,
          value: item.locationId,
        }));

        setLocations(transformedArray);
      } else {
        // show error message
        toaster.toastError(data.message);
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message);
    }
  };

  // Update the data
  const handleUpdate = async () => {
    await updateData({
      heading: carwash_owner ? "Manage Subscriptions" : company.companyName,
      url: `/superadmin/${pageURLs.carwash_locations}/${params.id}`,
      showBackButton: carwash_owner ? false : true,
      showMainMenu: true,
      activeLink: carwash_owner ? "manage-subscriptions" : "manage-car-wash",
    });
  };

  const getSubscriptions = async (pageNo = 1, subscriptionType: string,bayType:number) => {
    try {
      setLoading(true)
      let param;
      const selectedLocationIds = selectedLocation
        .map((loc) => Number(loc.value))
        .filter((id) => Number.isFinite(id));
      const selectedLocationParam = selectedLocationIds.join(",");

      const isBuyupsInbay = subscriptionType === "buyups" && bayType === serviceType.Inbay;
      if (isBuyupsInbay && carwash_owner) {
        param =
          selectedLocationIds.length > 0
            ? `?locationId=${selectedLocationParam}`
            : "";
      } else if (isBuyupsInbay && !carwash_owner) {
        const carWashId = params.id ? parseInt(params.id) : 0;
        param = `?carWashId=${carWashId}${
          selectedLocationIds.length > 0
            ? `&locationId=${selectedLocationParam}`
            : ""
        }`;
      } else if (selectedLocation.length) {
        const locationIds = selectedLocation.map((loc) => {
          return { location_id: loc.value };
        });
        const locationIdsString = encodeURIComponent(
          JSON.stringify(locationIds)
        );
        if (carwash_owner) {
          param = `?locations=${locationIdsString}&subscriptionType=${subscriptionType}&bayType=${bayType}&pageNo=${pageNo} &sortOrder=1&sortField=1`;
        } else {
          param = `?locations=${locationIdsString}&subscriptionType=${subscriptionType}&bayType=${bayType}&pageNo=${pageNo}&carWashId=${
            params.id ? parseInt(params.id) : 0
          }&sortOrder=1&sortField=1`;
        }
      } else {
        if (carwash_owner) {
          param = `?subscriptionType=${subscriptionType}&bayType=${bayType}&pageNo=${pageNo}&sortOrder=1&sortField=1`;
        } else {
          param = `?subscriptionType=${subscriptionType}&bayType=${bayType}&pageNo=${pageNo}&carWashId=${
            params.id ? parseInt(params.id) : 0
          }&sortOrder=1&sortField=1`;
        }
      }
      
      const listUrl = carwash_owner && subscriptionType === "buyups"
        ? SubscriptionApi.CARWASH_OWNER_LIST_BUY_UPS :
        !carwash_owner && subscriptionType === "buyups"
        ? SubscriptionApi.LIST_BUY_UPS
        : carwash_owner
        ? SubscriptionApi.CARWASH_OWNER_LIST_SUBSCRIPTIONS
        : SubscriptionApi.LIST_SUBSCRIPTIONS;
      const { data } = await axios.get(listUrl + param);
      const isOwnerBuyUps = subscriptionType === "buyups" ? true : false;
      const rawList = isOwnerBuyUps ? data?.data ?? [] : data?.data?.list ?? [];
      if (data?.error === 0 && Array.isArray(rawList)) {
        const subscription_list: SubscriptionItem[] = isOwnerBuyUps
          ? rawList.map((item: any) => ({
              SubscriptionID: item?.BuyUpID ?? 0,
              WashName: item?.BuyUpName ?? "",
              SubscriptionDetails: item?.BuyUpDetails ?? "",
              Price: item?.BuyUpPrice ?? 0,
              LocationName: item?.LocationNames ?? "",
            }))
          : rawList;
        // Check if location_list is empty
        if (subscription_list.length > 0) {
          if (isOwnerBuyUps) {
            setBuyUps(subscription_list);
            setSubscriptions([]);
          } else {
            setSubscriptions(subscription_list);
            setBuyUps([]);
          }
          if (isOwnerBuyUps) {
            setTotalPages(1);
          } else {
            const totalCount = data?.data?.totalCount ?? subscription_list.length;
            const limit = (data?.data?.limit ?? subscription_list.length) || 1;
            setTotalPages(Math.ceil(totalCount / limit) || 1);
          }
        } else {
          console.log("Subscription list is empty");
          if (isOwnerBuyUps) {
            setBuyUps([]);
          } else {
            setSubscriptions([]);
          } // Set to an empty array if subscription_list is empty
        }
      } else {
        console.log("Subscription list is not available or is not an array");
        if (carwash_owner && subscriptionType === "buyups") {
          setBuyUps([]);
        } else {
          setSubscriptions([]);
        } // Set to an empty array if data is not available
      }
      setLoading(false);
    } catch (error: any) {
      console.error("Error fetching company list:", error.message);
      setLoading(false);
      throw new Error(error.response?.data?.message || error.message);
    }
  };

  const handleSelectChange = (selectedOption: any) => {
    // Update selected location
    setSelectedLocation(selectedOption);
  };

  // Handle switch change by index
  const handleSwitchChange = (index: number, wash_type: number) => {
    const message = `Do you really want to change the status ?`;
    setModalAction("switch");
    setPendingIndex(index);
    setPendingType(wash_type);
    setModalMessage(message);
    setShowSubscriptionModal(true);
  };

  const confirmSwitchChange = () => {
    if (pendingIndex !== null && pendingType !== null) {
      const updatedSubscription = subscriptions
        .filter((item) => item.SubscriptionType === pendingType)
        .map((item, i) => {
          if (i === pendingIndex) {
            if (item.IsActive === 1) {
              return {
                ...item,
                IsActive: 0,
              };
            } else {
              return {
                ...item,
                IsActive: 1,
              };
            }
          }
          return item;
        });

      // To append remaining wash type to subscriptions array
      const otherWashtypeData = subscriptions.filter(
        (item) => item.SubscriptionType !== pendingType
      );

      // Update state
      setSubscriptions(updatedSubscription.concat(otherWashtypeData));
      handleOnHide();
    }
  };

  // Navigate accordung to url
  const itemClick = async (url: string) => {
    navigate(url);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const getVisiblePageNumbers = () => {
    const totalPageNumbersToShow = 5;
    let startPage = Math.max(
      1,
      currentPage - Math.floor(totalPageNumbersToShow / 2)
    );
    let endPage = startPage + totalPageNumbersToShow - 1;

    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - totalPageNumbersToShow + 1);
    }

    const pageNumbers = [];
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  // To handle tab change
  const onTabChange = async (type: any) => {
    setLoading(true)
    setCurrentPage(1);
    if (type === "single-wash") 
      {
        setSubscriptionType("day");
      }
    else if(type === "bulk-single-wash")
    {
      setSubscriptionType("bulk");
    }
    else if (type === "buy-ups" && bayType === serviceType.Inbay)
    {
      setSubscriptionType("buyups");
    }
    else setSubscriptionType("month");
  };

  const addSubscription = () => {
    if (carwash_owner) {
      if (subscriptionType == "buyups")
      {
          navigate(`/${pageURLs.add_buy_ups}`,{state:{bayType:bayType}});
          return;
      }
      if(subscriptionType == "bulk")
      {
          navigate(`/${pageURLs.add_bulk_singlewash}`,{state:{bayType:bayType}});
      }
      else if (bayType == serviceType.Inbay) {
        navigate(`/${pageURLs.add_subscription}/${subscriptionType}`,{state:{bayType:bayType}});
      }
      else   {
        navigate(`/${pageURLs.add_self_serve_subscription}/${subscriptionType}`,{state:{bayType:bayType}});
      }

    } else if(!carwash_owner){
      if (subscriptionType == "buyups")
      {
        navigate(`/superadmin/${pageURLs.add_buy_ups}/${params.id}`,{state:{bayType:bayType}});
        return;
      }
      if (bayType == serviceType.Inbay) {
        navigate(
          `/superadmin/${pageURLs.add_subscription}/${params.id}/${subscriptionType}`,{state:{bayType:bayType}}
        );
      }
      else   {
        navigate(`/superadmin/${pageURLs.add_self_serve_subscription}/${params.id}/${subscriptionType}`,{state:{bayType:bayType}});
      }

    }
  };

  const viewRelayDetails = (subscriptionId: number) => {
    if (!carwash_owner) {
      navigate(
        `/superadmin/${pageURLs.add_relaynumber}/${params.id}/${subscriptionId}?type=${subscriptionType}&baySubscriptionType=${bayType}`,
        {
          state: { origin: "manage-subscription" },
        }
      );
    } else {
      navigate(
        `/${pageURLs.add_relaynumber}/${subscriptionId}?type=${subscriptionType}&baySubscriptionType=${bayType}`,
        {
          state: { origin: "manage-subscription" },
        }
      );
    }
  };

  const onEdit = (subscription: SubscriptionItem, washtype: string) => {
    const subscriptionId = subscription.SubscriptionID;
    if (!carwash_owner) {
      if (subscriptionType === "buyups") {
        navigate(
          `/superadmin/${pageURLs.edit_buyups}/${params.id}/${subscriptionId}`,
          { state: { bayType: bayType, buyup: subscription } }
        );
        return;
      }
      if(bayType== serviceType.Inbay)
      {
        navigate(
          `/superadmin/${pageURLs.edit_subscription}/${params.id}/${subscriptionId}`,
          {
            state: { origin: washtype ,bayType:bayType},
          }
        );
      }
      else {
        navigate(
          `/superadmin/${pageURLs.edit_self_serve_subscription}/${params.id}/${subscriptionId}`,
          {
            state: { origin: washtype ,bayType:bayType},
          }
        );
      }
      
    } else {
      if(subscriptionType=="buyups")
      {
        navigate(
          `/${pageURLs.edit_buyups}/${subscriptionId}`,{state:{bayType:bayType, buyup: subscription}}
        );
        return;
      }
      if(subscriptionType=="bulk")
      {
        navigate(
        `/${pageURLs.edit_bulk_singlewash}/${subscriptionId}`,{state:{bayType:bayType}}
      );
      }
      else if(bayType== serviceType.Inbay)
      {
      navigate(
        `/${pageURLs.edit_subscription}/${subscriptionId}?type=${subscriptionType}`,{state:{bayType:bayType}}
      );
    }
    else {
      navigate(
        `/${pageURLs.edit_self_serve_subscription}/${subscriptionId}?type=${subscriptionType}`,{state:{bayType:bayType}}
      );
    }
  }
  };

  const onDeleteBuyup = (buyupId: number) => {
    if (!buyupId) {
      toaster.toastError("Invalid Up Sell.");
      return;
    }

    setModalAction("deleteBuyup");
    setPendingBuyupId(buyupId);
    setModalMessage("Do you really want to delete this Up Sell?");
    setShowSubscriptionModal(true);
  };

  const executeDeleteBuyup = async () => {
    if (!pendingBuyupId) {
      toaster.toastError("Invalid Up Sell.");
      handleOnHide();
      return;
    }

    try {
      handleOnHide();
      setLoading(true);
      const payload = { buyupId: pendingBuyupId };
      const { data } = carwash_owner
        ? await axios.post(SubscriptionApi.CARWASH_OWNER_DELETE_BUY_UP, payload)
        : await axios.post(SubscriptionApi.DELETE_BUY_UPS, payload);

      if (data?.error === 0) {
        toaster.toastSuccess(data?.message || "Up Sell deleted successfully.");
        await getSubscriptions(currentPage, subscriptionType, bayType);
      } else {
        toaster.toastError(data?.message || "Failed to delete Up Sell.");
      }
    } catch (error: any) {
      toaster.toastError(error?.response?.data?.message || error?.message);
    } finally {
      setLoading(false);
    }
  };

  const onDeleteSingleWash = (subscriptionId: number) => {
    if (!subscriptionId) {
      toaster.toastError("Invalid Single Wash.");
      return;
    }

    setModalAction("deleteSingleWash");
    setPendingSubscriptionId(subscriptionId);
    setModalMessage("Do you really want to delete this Single Wash?");
    setShowSubscriptionModal(true);
  };

  const executeDeleteSingleWash = async () => {
    const subscriptionId = pendingSubscriptionId;
    if (!subscriptionId) {
      toaster.toastError("Invalid Single Wash.");
      handleOnHide();
      return;
    }

    try {
      handleOnHide();
      setLoading(true);
      const payload = { subscriptionId };
      const { data } = carwash_owner
        ? await axios.post(SubscriptionApi.CARWASH_OWNER_DELETE_SINGLE_WASH, payload)
        : await axios.post(SubscriptionApi.DELETE_SINGLE_WASH, payload);

      if (data?.error === 0) {
        toaster.toastSuccess(data?.message || "Single Wash deleted successfully.");
        await getSubscriptions(currentPage, subscriptionType, bayType);
      } else {
        toaster.toastError(data?.message || "Failed to delete Single Wash.");
      }
    } catch (error: any) {
      toaster.toastError(error?.response?.data?.message || error?.message);
    } finally {
      setLoading(false);
    }
  };

  const confirmModalAction = () => {
    if (modalAction === "deleteBuyup") {
      executeDeleteBuyup();
      return;
    }
    if (modalAction === "deleteSingleWash") {
      executeDeleteSingleWash();
      return;
    }
    confirmSwitchChange();
  };

  // Truncate text function to limit the length of the description displayed
  const truncateText = (text: string, maxLength: number): string => {
    return text.length > maxLength
      ? text.substring(0, maxLength) + "..."
      : text;
  };
  // Render tooltip function for the full address
  const renderTooltip = (fullAddress: string) => {
    const TooltipComponent = (props: any) => (
      <Popover id="my-popover-id" {...props}> 
      <Popover.Body><span  dangerouslySetInnerHTML={{ __html: fullAddress }}></span></Popover.Body></Popover>
      // <Tooltip {...props} dangerouslySetInnerHTML={{ __html: fullAddress }}>{fullAddress}</Tooltip>
    );
    TooltipComponent.displayName = `Tooltip(${fullAddress})`; // Dynamic display name
    return TooltipComponent;
  };

  const changeBayType = (event: React.ChangeEvent<HTMLInputElement>) => {
    setbayType(Number(event.target.value));
  }

    const fetchSetting = async () => {
      try {
        const res = await axios.get(CompanyApi.GET_MULTILOCATION_REPORT_SETTINGS);
        if (res?.data?.error === 0 && res.data?.data?.hasMultipleLocationFeature !== undefined) {
          setIsMultilocationReportRequired(res.data.data.hasMultipleLocationFeature === 1);
        } else {
          toaster.toastError('Failed to load car info settings');
        }
      } catch (err) {
        toaster.toastError('Error fetching car info settings');
      } finally {
        setLoading(false);
      }
    };
  
 const handleSwitchChangeMultilocation = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.checked;
    setIsMultilocationReportRequired(newValue);

    try {
      setLoading(true);
      const payload = {
        hasMultipleLocationFeature: newValue ,
      };

      const res = await axios.post(CompanyApi.MULTILOCATION_REPORT_SETTINGS, payload);

      if (res?.data?.error === 0) {
        toaster.toastSuccess(res?.data?.message);
        window.location.reload()
      } else {
        toaster.toastError(res?.data?.message || "Failed to update setting");
      }
    } catch (err) {
      toaster.toastError("API error while updating");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="manage-carwash-container manage-subscription-container">
      <Container>
        
        {carwash_owner ? null : (
          <Breadcrumb>
            <Breadcrumb.Item
              onClick={() => {
                itemClick(`/superadmin/${pageURLs.manage_carwash}`);
              }}
            >
              Manage Car Washes
            </Breadcrumb.Item>
            <Breadcrumb.Item
              onClick={() => {
                itemClick(
                  `/superadmin/${pageURLs.carwash_locations}/${params.id}`
                );
              }}
            >
              {company.companyName}
            </Breadcrumb.Item>
            <Breadcrumb.Item active>
              {subscriptionType === "bulk"? "Manage Prepaid Discount Package":subscriptionType === "day"
                ? "Manage Single Wash"
                : "Manage Subscriptions"}
            </Breadcrumb.Item>
          </Breadcrumb>
        )}
        {carwash_owner && !isLocationManager ? <div className="car-info-sec d-flex flex-column align-items-end">
            <div className="info-switch d-flex align-items-center gap-3">
              <h5 className="f-bold">Multi Location Report</h5>
              <CWSwitch
                id="statusswitch"
                name="statusswitch"
                checked={isMultilocationReportRequired}
                onChange={handleSwitchChangeMultilocation}
              />
            </div>
            <div className="desc">
              <span className="fw-bold">Note:</span> Enable this to show multi location report in report section.
            </div>
          </div>:""}
        {carwash_owner ? null : (
          <div className="manage-activity-wrap d-flex justify-content-between align-items-center">
            {companyDetails && (
              <GenerateLink id={params.id} companyDetails={companyDetails} />
            )}
            <div className="activity-status d-flex">
              <CarWashStatus
                carWashId={params.id}
                status={companyDetails?.IsCompanyActive == 1 ? true : false}
                companyName={company.companyName}
              />
              <div className="stat-group d-flex align-items-center">
                <p>Subscription Status</p>
                <Badge
                  className={subscriptionActive === 1 ? "active" : "inactive"}
                >
                  {subscriptionActive === 1
                    ? "Active"
                    : subscriptionActive === 0
                    ? "Inactive"
                    : "N/A"}
                </Badge>
              </div>
            </div>
          </div>
        )}
        <div className="manage-activity-wrap self-serve-selection d-flex justify-content-between align-items-center">
          <div className="radio-field-group d-flex">
            <CWRadio
              id="baytunnel"
              name="hasDefaultWash"
              label="In Bay/Tunnel"
              checked={bayType===serviceType.Inbay}
              onChange={changeBayType}
              value={serviceType.Inbay}
            />
            <CWRadio id="selfserve" name="hasDefaultWash" label="Self Serve" onChange={changeBayType} value={serviceType.SelfServe} checked={bayType===serviceType.SelfServe}/>
            <CWRadio id="dogwash" name="hasDefaultWash" label="Dog Wash" onChange={changeBayType} value={serviceType.DogWash} checked={bayType===serviceType.DogWash}/>
            <CWRadio id="vacuum" name="hasDefaultWash" label="Vacuum" onChange={changeBayType} value={serviceType.Vacuum} checked={bayType===serviceType.Vacuum}/>

          </div>
        </div>

        <div className="manage-header-block d-flex justify-content-between align-items-center">
          <Button
            title={
              subscriptionType === "day"
                ? (bayType==serviceType.Vacuum ? "Add Single Use" :"Add Single Wash")
                : subscriptionType === "bulk"
                ? "Add Prepaid Discount Package"
                : subscriptionType === "buyups"
                ? "Add Up Sell"
                : "Add Subscription"
            }
            type="submit"
            variant="primary"
            className="primary-btn ms-auto d-flex align-items-center"
            onClick={() => addSubscription()}
          >
            {subscriptionType === "day"
              ? (bayType==serviceType.Vacuum ? "Add Single Use" :"Add Single Wash")
              : subscriptionType === "bulk"
              ? "Add Prepaid Discount Package"
              : subscriptionType === "buyups"
              ? "Add Up Sell"
              : "Add Subscription"}
            <AddIcon />
          </Button>
        </div>
        <div className="cw-main-tab position-relative">
          <div className="select-location">
            {/* <CWSelect
                            id="location"
                            name="location"
                            options={options}
                            label="Select Location"
                            dropdownGap={0}
                            dropdownPosition="auto"
                            placeholder="Select Location"
                            values={selectedLocation ? [selectedLocation] : []}
                            multi={true}
                            onChange={(selectedOptions) => handleSelectChange(selectedOptions[0])}
                        /> */}
            <CWMultiSelect
              id="location"
              name="location"
              label="Select Location"
              options={locations}
              dropdownGap={0}
              dropdownPosition="auto"
              placeholder="Select Location"
              onChange={(selectedOptions) =>
                handleSelectChange(selectedOptions)
              }
              hasSelectAll={true}
              value={selectedLocation}
              labelAllSelected="All Locations Selected."
            />
          </div>
          <Tabs
            activeKey={
              subscriptionType === "day"
                ? "single-wash"
                : subscriptionType === "bulk"
                ? "bulk-single-wash"
                : subscriptionType === "buyups" && bayType === serviceType.Inbay
                ? "buy-ups"
                : "subscriptions"
            }
            id="subscription-tab"
            className="cw-tab-title justify-content-end"
            onSelect={(k) => onTabChange(k)}
          >
            <Tab eventKey="subscriptions" title="Subscriptions">
              <div className="cw-table-wrap">
                <Table responsive className="m-0">
                  <thead>
                    <tr>
                      <th>Wash Name</th>
                      {bayType == serviceType.Inbay ? <th>Subscription Details</th> :""}
                      {bayType == serviceType.Inbay ? <th>No. of Washes</th>:""}
                      {bayType != serviceType.Inbay ? <th>Minutes available</th> : ""}
                      <th>Price</th>
                      <th>Car Wash Locations</th>
                      <th>Subscription Type</th>
                      <th>Subscription Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ?<SmallLoader/>:   subscriptions &&
                    subscriptions.filter((item) => item.SubscriptionType === 2)
                      .length ? (
                      subscriptions
                        .filter((item) => item.SubscriptionType === 2)
                        .map((item, index) => (
                          <tr key={index}>
                            <td>{item.WashName}</td>
                            {bayType == serviceType.Inbay ?<td>
                              {item.SubscriptionDetails ? (
                                <>
                                  <OverlayTrigger
                                    placement="top"
                                    overlay={renderTooltip(
                                      item.SubscriptionDetails
                                    )} // Pass the full Description to the tooltip
                                  >
                                    <span dangerouslySetInnerHTML={{ __html: truncateText(
                                        item.SubscriptionDetails,
                                        20
                                      ) }}>
                                
                                    </span>
                                  </OverlayTrigger>
                                </>
                              ) : (
                                ""
                              )}
                            </td>:""}
                            {bayType == serviceType.Inbay ? <td>{item.NumberOfWashes}</td>:""}
                            {bayType != serviceType.Inbay ?<td>{item.WashTime}</td>:""}
                            <td>${item.Price}</td>
                            <td> {item.LocationName}</td>
                            <td>
                              {item.SubscriptionType === 2
                                ? "Monthly"
                                : item.SubscriptionType === 1
                                ? "Single"
                                : "Yearly"}
                            </td>
                            <td className="nowrap">
                              <div className="active-group d-flex align-items-center">
                                <Badge
                                  className={
                                    item.IsActive === 0 ? "inactive" : "active"
                                  }
                                >
                                  {item.IsActive === 0 ? "Inactive" : "Active"}
                                </Badge>
                                {/* <CWSwitch
                                                                id="statusswitch"
                                                                name="statusswitch"
                                                                checked={item.IsActive === 1 ? true : false}
                                                                onChange={() => handleSwitchChange(index, item.SubscriptionType)}
                                                            /> */}
                              </div>
                            </td>
                            <td className="nowrap">
                              <div className="active-group d-flex align-items-center">
                                {bayType == serviceType.Inbay ?
                                <Button
                                  title="View Relay Details"
                                  variant="outline-primary"
                                  className="outline-btn me-3"
                                  onClick={() =>
                                    viewRelayDetails(item.SubscriptionID)
                                  }
                                >
                                  View Relay Details
                                </Button> :""}
                                <Button
                                  title="Copy URL"
                                  variant="outline-primary"
                                  className="outline-btn me-3"
                                  onClick={() =>
                                    handleCopyUrlClick(item.shareUrl)
                                  }
                                >
                                  Copy URL
                                </Button>
                                <OverlayTrigger
                                  placement="top"
                                  overlay={<Tooltip>Edit</Tooltip>}
                                >
                                  <Button
                                    variant="light"
                                    className="light-btn action-btn"
                                    onClick={() =>
                                      onEdit(
                                        item,
                                        "Subscriptions"
                                      )
                                    }
                                  >
                                    <EditIcon />
                                  </Button>
                                </OverlayTrigger>
                                {/* <OverlayTrigger
                                                                placement="top"
                                                                overlay={
                                                                    <Tooltip>Delete</Tooltip>
                                                                }
                                                            >
                                                                <Button variant="light" className="light-btn action-btn" onClick={() => alert("Not Implemented.")}><DeleteIcon /></Button>
                                                            </OverlayTrigger> */}
                              </div>
                            </td>
                          </tr>
                        ))
                    ) : (
                      <tr>
                        <td style={{ textAlign: "center" }} colSpan={7}>
                          No data found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
                {subscriptions.filter((item) => item.SubscriptionType === 2)
                  .length ? (
                  <div className="mt-3 d-flex justify-content-end">
                    <Pagination>
                      <Pagination.First
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(1)}
                      />
                      <Pagination.Prev
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(currentPage - 1)}
                      />
                      {getVisiblePageNumbers().map((pageNumber) => (
                        <Pagination.Item
                          key={pageNumber}
                          active={pageNumber === currentPage}
                          onClick={() => handlePageChange(pageNumber)}
                        >
                          {pageNumber}
                        </Pagination.Item>
                      ))}
                      <Pagination.Next
                        disabled={currentPage === totalPages}
                        onClick={() => handlePageChange(currentPage + 1)}
                      />
                      <Pagination.Last
                        disabled={currentPage === totalPages}
                        onClick={() => handlePageChange(totalPages)}
                      />
                    </Pagination>
                  </div>
                ) : null}
              </div>
            </Tab>
            <Tab eventKey="single-wash" title={(bayType==serviceType.Vacuum ? "Single Use" :"Single Wash")}>
              <div className="cw-table-wrap">
                <Table responsive className="m-0">
                  <thead>
                    <tr>
                      <th>Wash Name</th>
                      {bayType == serviceType.Inbay ?<th>Subscription Details</th>:""}
                      {/* <th>No. of Washes</th> */}
                      <th>Price</th>
                      <th>Car Wash Locations</th>
                      <th>Subscription Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    { loading ?<SmallLoader/>:  subscriptions.filter((item) => item.SubscriptionType === 1)
                      .length ? (
                      subscriptions
                        .filter((item) => item.SubscriptionType === 1)
                        .map((item, index) => (
                          <tr key={index}>
                            <td>{item.WashName}</td>
                            {bayType == serviceType.Inbay ?<td>
                              {item.SubscriptionDetails ? (
                                <>
                                  <OverlayTrigger
                                    placement="top"
                                    overlay={renderTooltip(
                                      item.SubscriptionDetails
                                    )} // Pass the full Description to the tooltip
                                  >
                                    <span dangerouslySetInnerHTML={{ __html: truncateText(
                                        item.SubscriptionDetails,
                                        20
                                      ) }}>
                                
                                    </span>
                                  </OverlayTrigger>
                                </>
                              ) : (
                                ""
                              )}
                            </td>:""}
                            {/* <td>{item["No. of Washes"]}</td> */}
                            <td>${item.Price}</td>
                            <td>{item.LocationName}</td>

                            <td className="nowrap">
                              <div className="active-group d-flex align-items-center">
                                <Badge
                                  className={
                                    item.IsActive === 1 ? "active" : "inactive"
                                  }
                                >
                                  {item.IsActive === 1 ? "Active" : "Inactive"}
                                </Badge>
                                {/* <CWSwitch
                                                                id="statusswitch"
                                                                name="statusswitch"
                                                                checked={item.IsActive === 1 ? true : false}
                                                                onChange={() => handleSwitchChange(index, item.SubscriptionType)}
                                                            /> */}
                              </div>
                            </td>
                            <td className="nowrap">
                              <div className="active-group d-flex align-items-center">
                              {bayType == serviceType.Inbay ?<Button
                                  title="View Relay Details"
                                  variant="outline-primary"
                                  className="outline-btn me-3"
                                  onClick={() =>
                                    viewRelayDetails(item.SubscriptionID)
                                  }
                                >
                                  View Relay Details
                                </Button>:""}
                                <Button
                                  title="Copy URL"
                                  variant="outline-primary"
                                  className="outline-btn me-3"
                                  onClick={() =>
                                    handleCopyUrlClick(item.shareUrl)
                                  }
                                >
                                  Copy URL
                                </Button>
                                <Button
                                  title="Edit"
                                  variant="light"
                                  className="light-btn action-btn"
                                  onClick={() =>
                                    onEdit(item, "Single Wash")
                                  }
                                >
                                  <EditIcon />
                                </Button>
                                <Button
                                  title="Delete"
                                  variant="light"
                                  className="light-btn action-btn"
                                  onClick={() =>
                                    onDeleteSingleWash(item.SubscriptionID)
                                  }
                                >
                                  <DeleteIcon />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                    ) : (
                      <tr>
                        <td style={{ textAlign: "center" }} colSpan={7}>
                          No data found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
                {subscriptions.filter((item) => item.SubscriptionType === 1)
                  .length ? (
                  <div className="mt-3 d-flex justify-content-end">
                    <Pagination>
                      <Pagination.First
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(1)}
                      />
                      <Pagination.Prev
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(currentPage - 1)}
                      />
                      {getVisiblePageNumbers().map((pageNumber) => (
                        <Pagination.Item
                          key={pageNumber}
                          active={pageNumber === currentPage}
                          onClick={() => handlePageChange(pageNumber)}
                        >
                          {pageNumber}
                        </Pagination.Item>
                      ))}
                      <Pagination.Next
                        disabled={currentPage === totalPages}
                        onClick={() => handlePageChange(currentPage + 1)}
                      />
                      <Pagination.Last
                        disabled={currentPage === totalPages}
                        onClick={() => handlePageChange(totalPages)}
                      />
                    </Pagination>
                  </div>
                ) : null}
              </div>
            </Tab>
            <Tab eventKey="bulk-single-wash" title="Prepaid Discount Packages">
              <div className="cw-table-wrap">
                <Table responsive className="m-0">
                  <thead>
                    <tr>
                      <th>Wash Name</th>
                      {bayType == serviceType.Inbay ?<th>Subscription Details</th>:""}
                      {/* <th>No. of Washes</th> */}
                      <th>Price</th>
                      <th>Car Wash Locations</th>
                      <th>Subscription Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ?<SmallLoader/>:  subscriptions.filter((item) => item.SubscriptionType === 3)
                      .length ? (
                      subscriptions
                        .filter((item) => item.SubscriptionType === 3)
                        .map((item, index) => (
                          <tr key={index}>
                            <td>{item.WashName}</td>
                            {/* {bayType == serviceType.Inbay ?<td>{item.SubscriptionDetails} </td>:""} */}
                            {bayType == serviceType.Inbay ?<td>
                              {item.SubscriptionDetails ? (
                                <>
                                  <OverlayTrigger
                                    placement="top"
                                    overlay={renderTooltip(
                                      item.SubscriptionDetails
                                    )} // Pass the full Description to the tooltip
                                  >
                                    <span dangerouslySetInnerHTML={{ __html: truncateText(
                                        item.SubscriptionDetails,
                                        20
                                      ) }}>
                                
                                    </span>
                                  </OverlayTrigger>
                                </>
                              ) : (
                                ""
                              )}
                            </td>:""}
                            {/* <td>{item["No. of Washes"]}</td> */}
                            <td>${item.Price}</td>
                            <td>{item.LocationName}</td>

                            <td className="nowrap">
                              <div className="active-group d-flex align-items-center">
                                <Badge
                                  className={
                                    item.IsActive === 1 ? "active" : "inactive"
                                  }
                                >
                                  {item.IsActive === 1 ? "Active" : "Inactive"}
                                </Badge>
                                {/* <CWSwitch
                                                                id="statusswitch"
                                                                name="statusswitch"
                                                                checked={item.IsActive === 1 ? true : false}
                                                                onChange={() => handleSwitchChange(index, item.SubscriptionType)}
                                                            /> */}
                              </div>
                            </td>
                            <td className="nowrap">
                              <div className="active-group d-flex align-items-center">
                              {bayType == serviceType.Inbay ?<Button
                                  title="View Relay Details"
                                  variant="outline-primary"
                                  className="outline-btn me-3"
                                  onClick={() =>
                                    viewRelayDetails(item.SubscriptionID)
                                  }
                                >
                                  View Relay Details
                                </Button>:""}
                                <Button
                                  title="Copy URL"
                                  variant="outline-primary"
                                  className="outline-btn me-3"
                                  onClick={() =>
                                    handleCopyUrlClick(item.shareUrl)
                                  }
                                >
                                  Copy URL
                                </Button>
                                <Button
                                  title="Edit"
                                  variant="light"
                                  className="light-btn action-btn"
                                  onClick={() =>
                                    onEdit(item, "Single Wash")
                                  }
                                >
                                  <EditIcon />
                                </Button>
                                <Button
                                  title="Delete"
                                  variant="light"
                                  className="light-btn action-btn"
                                  onClick={() => alert("Not Implemented.")}
                                >
                                  <DeleteIcon />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                    ) : (
                      <tr>
                        <td style={{ textAlign: "center" }} colSpan={7}>
                          No data found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
                {subscriptions.filter((item) => item.SubscriptionType === 3)
                  .length ? (
                  <div className="mt-3 d-flex justify-content-end">
                    <Pagination>
                      <Pagination.First
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(1)}
                      />
                      <Pagination.Prev
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(currentPage - 1)}
                      />
                      {getVisiblePageNumbers().map((pageNumber) => (
                        <Pagination.Item
                          key={pageNumber}
                          active={pageNumber === currentPage}
                          onClick={() => handlePageChange(pageNumber)}
                        >
                          {pageNumber}
                        </Pagination.Item>
                      ))}
                      <Pagination.Next
                        disabled={currentPage === totalPages}
                        onClick={() => handlePageChange(currentPage + 1)}
                      />
                      <Pagination.Last
                        disabled={currentPage === totalPages}
                        onClick={() => handlePageChange(totalPages)}
                      />
                    </Pagination>
                  </div>
                ) : null}
              </div>
            </Tab>
            {bayType === serviceType.Inbay ? (
            <Tab eventKey="buy-ups" title="Up Sell">
              <div className="cw-table-wrap">
                <Table responsive className="m-0">
                  <thead>
                    <tr>
                      <th>Up Sell Name</th>
                      <th>Up Sell Details</th>
                      <th>Price</th>
                      <th>Car Wash Locations</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ?<SmallLoader/>:  buyUps.length ? (
                      buyUps.map((item, index) => (
                          <tr key={index}>
                            <td>{item.WashName}</td>
                            <td>
                              {item.SubscriptionDetails ? (
                                <>
                                  <OverlayTrigger
                                    placement="top"
                                    overlay={renderTooltip(
                                      item.SubscriptionDetails
                                    )}
                                  >
                                    <span dangerouslySetInnerHTML={{ __html: truncateText(
                                        item.SubscriptionDetails,
                                        20
                                      ) }}>
                                
                                    </span>
                                  </OverlayTrigger>
                                </>
                              ) : (
                                ""
                              )}
                            </td>
                            <td>${item.Price}</td>
                            <td>{item.LocationName}</td>
                            <td className="nowrap">
                              <div className="active-group d-flex align-items-center">
                                <Button
                                   title="View Relay Details"
                                   variant="outline-primary"
                                   className="outline-btn me-3"
                                   onClick={() =>
                                     carwash_owner
                                       ? navigate(
                                           `/${pageURLs.add_buyup_relaynumber}/${item.SubscriptionID}?bayType=${bayType}`,
                                           { state: { type: "buyups" } }
                                         )
                                       : navigate(
                                           `/superadmin/${pageURLs.add_buyup_relaynumber}/${params.id}/${item.SubscriptionID}?bayType=${bayType}`,
                                           { state: { type: "buyups" } }
                                         )
                                   }
                                 >
                                   View Relay Details
                                 </Button>
                                {/* <Button
                                  title="Copy URL"
                                  variant="outline-primary"
                                  className="outline-btn me-3"
                                  onClick={() =>
                                    handleCopyUrlClick(item.shareUrl)
                                  }
                                >
                                  Copy URL
                                </Button> */}
                                <Button
                                  title="Edit"
                                  variant="light"
                                  className="light-btn action-btn"
                                  onClick={() =>
                                    onEdit(item, "Up Sell")
                                  }
                                >
                                  <EditIcon />
                                </Button>
                                <Button
                                  title="Delete"
                                  variant="light"
                                  className="light-btn action-btn"
                                  onClick={() =>
                                    onDeleteBuyup(item.SubscriptionID)
                                  }
                                >
                                  <DeleteIcon />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                    ) : (
                      <tr>
                        <td style={{ textAlign: "center" }} colSpan={5}>
                          No data found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Tab>
            ) : null}
          </Tabs>
        </div>
        <Modal
          show={showSubscriptionModal}
          onHide={handleOnHide}
          centered
          className="add-modal"
        >
          <Modal.Body className="text-center">
            <h5 className="f-bold text-center">{modalMessage}</h5>
            <div className="btn-sec d-grid">
              <Button
                title="Cancel"
                variant="light"
                className="secondary-btn"
                onClick={handleOnHide}
              >
                Cancel
              </Button>
              <Button
                title="Continue"
                variant="primary"
                className="primary-btn btn-main m-0"
                onClick={confirmModalAction}
              >
                Continue
              </Button>
            </div>
          </Modal.Body>
        </Modal>
        <Modal
          show={showCopyUrlModal}
          onHide={handleCloseCopyUrlModal}
          centered
          className="add-modal"
        >
          <Modal.Header closeButton></Modal.Header>
          <Modal.Body className="text-center">
            <h5 className="f-bold text-center">Product Purchase URL</h5>
            <p className="mt-3 mb-0">
            Copy this link and add it to your marketing website or promotional content. Customers who open this link will be taken directly to this package.
            </p>
            <CWInput
              id="productPurchaseUrl"
              name="productPurchaseUrl"
              type="text"
              // label="Purchase Redirection URL"
              value={selectedShareUrl}
              disabled
            />
            <div className="btn-sec d-grid mt-4">
              <Button
                title="Close"
                variant="light"
                className="secondary-btn"
                onClick={handleCloseCopyUrlModal}
              >
                Close
              </Button>
              <Button
                title="Copy URL"
                variant="primary"
                className="primary-btn btn-main m-0"
                onClick={handleCopyPurchaseUrl}
              >
                Copy URL
              </Button>
            </div>
          </Modal.Body>
        </Modal>
      </Container>
      {loading && <SmallLoader />}
    </div>
  );
};

export default ManageSubscriptions;
